<script lang="ts">
  import AppShell from './AppShell.svelte';
  import Icon from './Icon.svelte';
  import type { IconName } from '../lib/icons';

  type ItemKey =
    | 'funnels'
    | 'products'
    | 'data-tables'
    | 'data-duplicates'
    | 'data-import'
    | 'webhooks'
    | 'users'
    | 'audit';

  let {
    active,
    children,
  }: {
    active: ItemKey;
    children: import('svelte').Snippet;
  } = $props();

  interface NavItem {
    key: ItemKey;
    href: string;
    icon: IconName;
    label: string;
  }
  interface NavCategory {
    label: string;
    items: NavItem[];
  }

  const categories: NavCategory[] = [
    {
      label: 'Структура даних',
      items: [
        { key: 'funnels', href: '#/settings/funnels', icon: 'funnels', label: 'Воронки' },
        { key: 'products', href: '#/settings/products', icon: 'products', label: 'Продукти' },
        { key: 'data-tables', href: '#/settings/data-structure', icon: 'table', label: 'Управління таблицями' },
        { key: 'data-duplicates', href: '#/settings/data-structure/duplicates', icon: 'dedup', label: 'Визначення дублікатів' },
        { key: 'data-import', href: '#/settings/data-structure/import', icon: 'download', label: 'Імпорт даних' },
      ],
    },
    {
      label: 'Інтеграції',
      items: [
        { key: 'webhooks', href: '#/settings/webhooks', icon: 'webhook', label: 'Webhooks' },
      ],
    },
    {
      label: 'Команда',
      items: [
        { key: 'users', href: '#/settings/users', icon: 'users', label: 'Команда' },
        { key: 'audit', href: '#/audit', icon: 'audit', label: 'Audit log' },
      ],
    },
  ];
</script>

<AppShell active="settings">
  <!-- Break out of AppShell's p-7 so the secondary sidebar butts flush against the primary one. -->
  <div class="-mx-7 -my-7 flex min-h-[calc(100vh-3.5rem)]">
    <aside class="settings-secondary-nav w-64 shrink-0 border-r border-[var(--color-border-3)] bg-white px-3 pt-5 pb-6">
      <div class="px-2 mb-5 mt-1 flex items-center justify-between">
        <h2 class="text-[19px] font-bold leading-tight">Налаштування</h2>
      </div>

      {#each categories as cat (cat.label)}
        <div class="mb-4">
          <div class="px-2 pb-1.5 text-[9.5px] font-semibold uppercase tracking-[1.5px] text-[var(--color-text-muted-2)]">
            {cat.label}
          </div>
          {#each cat.items as item (item.key)}
            <a
              href={item.href}
              class="settings-nav-item"
              class:active={active === item.key}
            >
              <span class="settings-nav-icon inline-flex"><Icon name={item.icon} size={16} /></span>
              <span class="truncate">{item.label}</span>
            </a>
          {/each}
        </div>
      {/each}
    </aside>

    <div class="flex-1 min-w-0 overflow-x-auto px-7 py-7">
      {@render children()}
    </div>
  </div>
</AppShell>

<style>
  :global(.settings-nav-item) {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 10px;
    border-radius: 8px;
    font-size: 12.5px;
    color: var(--color-text);
    cursor: pointer;
    margin-bottom: 1px;
    text-decoration: none;
    font-weight: 500;
  }
  :global(.settings-nav-item:hover) {
    background: var(--color-pu-light);
    color: var(--color-pu-3);
  }
  :global(.settings-nav-item.active) {
    background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));
    color: white;
    box-shadow: 0 4px 10px rgba(123, 79, 190, 0.3);
  }
  :global(.settings-nav-item.active .settings-nav-icon) {
    color: white;
  }
  :global(.settings-nav-icon) {
    width: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-muted);
    flex-shrink: 0;
  }
</style>
